import {Injectable} from '@angular/core';
import {environment} from '../../../environments/environment';
import {IParamsImprimir} from '../interfaces/params-imprimir.interface';
import {AuthService} from './auth.service';
import {ImpresosEnum} from '../enums/imprimir.enum';
import {Router} from '@angular/router';
import {IParamsPrintPreview} from '../interfaces/paramsPrintPreview.interface';
import {Observable} from 'rxjs';
import {HttpClient, HttpEventType} from '@angular/common/http';
import {map} from 'rxjs/operators';
import {EmailData} from '../../shared/components/modal-email/interfaces/emailData.interface';
import {FormControl} from '@angular/forms';

@Injectable()
export class ImprimirService {
    private readonly defaultUrl = 'pages/core/viewer.aspx';

    constructor(
        private _authService: AuthService,
        private _router: Router,
        private _http: HttpClient
    ) {
    }

    descargarImpreso(printParams: IParamsImprimir, emailParams: EmailData, openInPreview = true, url: string = this.defaultUrl) {
        if (openInPreview) {
            // Las impresiones que no provengan del viewer.aspx de core no pasaran por el previsualizador
            if (url !== this.defaultUrl) {
                window.open(`${environment.ENTORNO}${url}`, '_blank');
            } else {
                this.openPreview({printParams, emailParams});
            }
        } else {
            const href = `${environment.ENTORNO}${url}${printParams ? this.prepareParams(printParams) : ''}`;
            const x = document.createElement("a");
            x.href = href;
            x.download = href;
            x.click();
        }
    }

    // Descarga y devuelve un mapa con el nombre y binario del pdf
    getPDF(printParams: IParamsImprimir, url: string = this.defaultUrl, background: any = null, isSigned: boolean = false): Observable<Map<string, string|Blob>> {
        const finalUrl = `${environment.ENTORNO}${url}${this.prepareParams(printParams, background, isSigned)}`;
        return this._http.get(finalUrl, {responseType: 'blob', observe: 'response'}).pipe(map(response => {
            const file = new Map();
            file.set('name', response.headers.get('content-disposition').split('"')[1]);
            file.set('data', new Blob([response.body], {type: "application/pdf"}))
            return file;
        }));
    }

    // Descarga y devuelve o el progreso del pdf o un mapa con el nombre y binario del pdf
    getPDFWithProgress(printParams: IParamsImprimir, url: string = this.defaultUrl, background: any = null, isSigned: boolean = false, marca: boolean = true): Observable<Map<string, any>> {
        const finalUrl = `${environment.ENTORNO}${url}${this.prepareParams(printParams, background, isSigned, marca)}`;

        return this._http.get(finalUrl, {observe: 'events', reportProgress: true, responseType: 'blob'}).pipe(
            map(event => {
                const response = new Map<string, any>()
                switch (event.type) {
                    case HttpEventType.DownloadProgress:
                        response.set('status', 'P');
                        response.set('progress', Math.round(100 * event.loaded / event.total));
                        break;
                    case HttpEventType.Response:
                        response.set('status', 'C');
                        const name = event.headers.get('content-disposition');
                        response.set('name', name ? name.split('"')[1] : 'Impreso');
                        response.set('data', new Blob([event.body], {type: "application/pdf"}));
                        break;
                    default:
                        response.set('status', 'P');
                        response.set('percentage', 0);
                        break;
                }
                return response;
            })
        );
    }

    // Codifica los parametros de impresion para formar la url
    prepareParams(printParams: IParamsImprimir, background: any = null, isSigned: boolean = null, printNotBrandChar: boolean = null): string {
        const paramsMap = new Map(Object.entries(printParams));
        let query = Array.from(paramsMap, param => `${param[0]}=${encodeURIComponent(param[1])}`).join('&');

        if (this.hasFilial(printParams) && !printParams.filial) {
            query += `&filial=${this._authService.getFilialComplete().filial}`;
        }

        if (this.hasBackground(printParams)) {
            // En caso de que el impreso lleve fondo pero no se especifique la configuracion, se cogera la configuracion por defecto que se haya seteado en el panel de control
            if (background === null) {
                const type = printParams.type === ImpresosEnum.or ? 'BackgroundOR' : 'BackgroundInvoice';
                background = {
                    backgroundLogo: this._authService.getBackgroundInvoice(type === 'BackgroundInvoice' ? 'BackgroundLogoInvoice' : 'BackgroundLogoOR'),
                    backgroundWorkshopInfo: this._authService.getBackgroundInvoice(type === 'BackgroundInvoice' ? 'BackgroundWorkshopInfoInvoice' : 'BackgroundWorkshopInfoOR'),
                    backgroundGrid: this._authService.getBackgroundInvoice(type === 'BackgroundInvoice' ? 'BackgroundGridInvoice' : 'BackgroundGridOR'),
                    backgroundLegalText: this._authService.getBackgroundInvoice(type === 'BackgroundInvoice' ? 'BackgroundLegalTextInvoice' : 'BackgroundLegalTextOR'),
                    backgroundLegalTextFoot: this._authService.getBackgroundInvoice(type === 'BackgroundInvoice' ? 'BackgroundLegalTextFootInvoice' : 'BackgroundLegalTextFootOR'),
                }
            }

            const backgroundAll: boolean = background.backgroundLogo && background.backgroundWorkshopInfo && background.backgroundGrid && background.backgroundLegalText && background.backgroundLegalTextFoot;

            // Si no se ha especificado si lleva o no fondo se cogera segun la configuracion por defecto que haya en el webconfig
            query += `&background=${backgroundAll}`;
            query += `&backgroundLogo=${background.backgroundLogo}`;
            query += `&backgroundWorkshopInfo=${background.backgroundWorkshopInfo}`;
            query += `&backgroundGrid=${background.backgroundGrid}`;
            query += `&backgroundLegalText=${background.backgroundLegalText}`;
            query += `&backgroundLegalTextFoot=${background.backgroundLegalTextFoot}`;

            query += `&printNotBrandChar=${printNotBrandChar === null ? this._authService.getSimboloSinMarca() : printNotBrandChar}`;
        }

        if (this.hasDigitalSignature(printParams)) {
            query += `&isSigned=${isSigned === null ? this._authService.getSignature() : isSigned}`;
        }

        return `?${query}`;
    }

    // Devuelve true si el impreso debe incluir la filial en los parametros
    private hasFilial(printParams: IParamsImprimir): boolean {
        // Listado de los impresos que NO llevan filial
        return ![
            ImpresosEnum.printInvoicePurchase, ImpresosEnum.printReceivedInvoice, ImpresosEnum.printFichajes, ImpresosEnum.printBonoPicajes,
            ImpresosEnum.printCarHistory, ImpresosEnum.printCorreccion, ImpresosEnum.printPedido, ImpresosEnum.printAlmacen,
            ImpresosEnum.printTomaInventario, ImpresosEnum.printBajaMasiva, ImpresosEnum.printDiferenciasInventario,
            ImpresosEnum.printValoradoInventario, ImpresosEnum.printPayment, ImpresosEnum.printPurchasePayment, ImpresosEnum.printAccountingConfig,
            ImpresosEnum.printReportSendGarantee, ImpresosEnum.printReportSendGaranteeSG, ImpresosEnum.printAccountEntryError,
            ImpresosEnum.printAccountEntryValid, ImpresosEnum.printAccountInvoiceNoExport, ImpresosEnum.printMarketing,
            ImpresosEnum.receipt, ImpresosEnum.printAppointmentsList, ImpresosEnum.printAppointmentConfirmation, ImpresosEnum.printHistorial,
            ImpresosEnum.printWarrantyIncident
        ].includes(printParams.type)
    }

    // Devuelve true si el impreso puede configurar si lleva o no fondo
    hasBackground(printParams: IParamsImprimir): boolean {
        return [
            ImpresosEnum.printInvoice, ImpresosEnum.printValoracion, ImpresosEnum.printPresupuestoTaller, ImpresosEnum.printGaranteeManualComp,
            ImpresosEnum.printAlbaran, ImpresosEnum.printPresupuestoVM, ImpresosEnum.printPartsInvoice, ImpresosEnum.printInvoiceGroupingValoration,
            ImpresosEnum.printNote, ImpresosEnum.printInvoicePreview, ImpresosEnum.printInvoiceGroupingDetail, ImpresosEnum.printInvoiceGroupingResumen,
            ImpresosEnum.printGroupingInvoices, ImpresosEnum.printPresupuestoOR, ImpresosEnum.or
        ].includes(printParams.type);
    }

    // Devuelve true si el impreso puede ser firmado digitalmente
    hasDigitalSignature(printParams: IParamsImprimir): boolean {
        // En el futuro se incluira tambien printPresupuestoOR
        return [
            ImpresosEnum.printInvoice, ImpresosEnum.printPresupuestoTaller, ImpresosEnum.printGaranteeManualComp, ImpresosEnum.printAlbaran,
            ImpresosEnum.printPresupuestoVM, ImpresosEnum.printPartsInvoice, ImpresosEnum.printInvoiceGroupingValoration, ImpresosEnum.printNote,
            ImpresosEnum.printInvoicePreview, ImpresosEnum.printInvoiceGroupingDetail, ImpresosEnum.printInvoiceGroupingResumen,
            ImpresosEnum.printGroupingInvoices
        ].includes(printParams.type);
    }

    openPreview(params: IParamsPrintPreview) {
        // Para evitar que los usuarios puedan ver y modificar los parametros de impresion facilmente en la barra de navegacion los codificamos a base64
        // como base64 en javascript esta limitado solo a caracteres ASCII, serializamos la url primero
        const url = this._router.serializeUrl(
            this._router.createUrlTree(['/printPreview'],
            { queryParams: {
                data: btoa(encodeURIComponent(JSON.stringify(params)))
            }})
        );
        window.open('#'+url, '_blank');
    }

}
